import { isEqual, maxBy, minBy, uniq, zip } from 'es-toolkit';
import { produce } from 'immer';

import { getUserVisibleOrDefaultExceptionMessage, isUserVisibleException } from '@paragon/client-tools';
import {
  BearingEnvelope,
  DEFAULT_BEVEL_CUT_TYPE_PREFERENCE,
  DEFAULT_ORDERED_CUTS,
  EnvelopeJustification,
  getComponentTypeForDefinitionType,
  getDefinitionTypeForComponentType,
  TopOrBottomCut,
  TrussDefinitionType,
  TrussEnvelope,
  TrussEnvelopeBevelCut,
  TrussType,
} from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import {
  convertDegreesToRadians,
  Direction2D,
  distanceLessThan,
  intersectSpanSets,
  Line2D,
  LineSegment2D,
  Plane3D,
  Point2D,
  reduceAndSortSpans,
  Span,
} from '@paragon/geometry-library';
import { point2DDistanceTo, point2DEquals, Point2DInterface } from '@paragon/geometry-library/source/scripts/Point2D';
import { convertPlaneToGeometricPlane, getEnvelopeSegment, getEnvelopeSegmentFromEnvelope } from '@paragon/layout';
import { enqueueError, enqueueInfo, enqueueWarning } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';
import {
  assertExhaustiveSwitch,
  forceArray,
  formatAsNaturalLanguageList,
  naturalSort,
  notNullish,
  pluralize,
  ReadonlyRecord,
} from '@paragon/utilities';

import {
  EnvelopeCalculationResult,
  EnvelopeRecalculationEnd,
} from '../../../library/layout-geometry/truss-envelopes/calculation/envelopeCalculationResult';
import { StageOneTrussEnvelope } from '../../../library/layout-geometry/truss-envelopes/calculation/stage1';
import { StageTwoTrussEnvelope } from '../../../library/layout-geometry/truss-envelopes/calculation/stage2';
import { UserSpecifiedBevelCut } from '../../../library/layout-geometry/truss-envelopes/calculation/stage3';
import {
  calculateTrussEnvelopeCapSetGroupings,
  CapSetState,
  generateCapSetProperties,
} from '../../../library/layout-geometry/truss-envelopes/capSets';
import { DEFAULT_FEATURE_PATTERN_TRUSS_ADDITIONAL_CUTS } from '../../../library/layout-geometry/truss-envelopes/defaults';
import { flipTrussEnvelope } from '../../../library/layout-geometry/truss-envelopes/flipTrussEnvelope';
import { FullyDefinedInsertingTrussEnvelopeData } from '../../../library/layout-geometry/truss-envelopes/insertion-information';
import { getTrussSolidCalculationDependencies } from '../../../library/layout-geometry/truss-envelopes/trussSolidSelectors';
import { getSelectedItemIdentifiers } from '../../../library/selection/selectors';
import { SelectableItemEntry } from '../../../library/selection/types';
import { findNextName } from '../../../library/utilities/findNextName';
import { getSingleSelectedItemEntryOrNull } from '../../selectors/getSelectedItemSelector';
import {
  getActiveLayoutLevelGuidOrNull,
  getAllTrussEnvelopeNames,
  getAllTrussEnvelopes,
  getVisibleCeilingPlaneCount,
  getVisibleRoofContainerGuids,
  getVisibleRoofPlaneCount,
} from '../../selectors/layoutDataSelectors';
import {
  getGeometricEcosystemForActivePortionOfLayout,
  getGeometricEcosystemScopedToSingleRoofContainer,
} from '../../selectors/layoutGeometricEcosystemSelectors';
import { isLayoutBasedOnRoofContainers } from '../../selectors/layoutModeSelectors';
import { getCalculatedRoofAndCeilingPlanes } from '../../selectors/roofAndCeilingPlaneSelectors';
import { getTrussEnvelopeInsertionInformation } from '../../selectors/trussEnvelopeInsertionInformationSelectors';
import {
  applicationOnInsertingCornerSet,
  applicationOnInsertingTrussEnvelope,
  applicationOnInsertingTrussEnvelopeRun,
  applicationOnPickingLayoutLine,
  applicationOnPickingPlane,
  applicationOnPickingRoofFace,
  clearInsertingObject,
  clearPickingObject,
  CornerSetType,
  InsertingCornerSetInfo,
  InsertingTrussEnvelopeData,
  NonInsertionTrussEnvelopeTargetInfo,
  resetInsertionTrussEnvelope,
  setInsertionCornerSetData,
  setInsertionTrussEnvelopeProperties,
  TrussEnvelopeTargetInfo,
} from '../../slices/applicationSlice';
import { setComponentDesignTimestamps } from '../../slices/componentDesignTimestampsSlice';
import { removeJackGirderConnections, setJackGirderConnections } from '../../slices/jackGirderConnectionsSlice';
import { layoutSlice } from '../../slices/layoutSlice';
import { closeRenameLayoutComponentsPanel, openRenameLayoutComponentsPanel } from '../../slices/panelDataSlice';
import { resetView } from '../cameraActions';
import { deleteComponentDesignsWithoutConfirmation, deleteItems } from '../deletionActions';
import { setComponentName } from '../editComponentActions';
import { ensureLevelIsVisible } from '../levelVisibilityActions';
import { receiveComponentDesign } from '../loadActions';
import { showFlipTrussEnvelopeDialog, showSelectComponentDesignDialog } from '../popupActions';
import { receiveActiveProjectSynchronizationPayload } from '../projectActions';
import { resetSelectedItems, selectItem, selectItemByGuid, selectItems } from '../selectActions';
import { AsyncThunkActionReturn, AsyncThunkActionVoid, ThunkActionReturn, ThunkActionVoid } from '../thunkActions';
import { saveBearingEnvelopes } from './bearingEnvelopeActions';

export const startInsertTrussEnvelope =
  (options?: {
    levelGuid?: string;
    roofContainerGuid?: string;
    featurePatternContainerGuid?: string;
  }): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    const selectedEntry = getSingleSelectedItemEntryOrNull(state);
    const insertionSettings = state.layout.trussEnvelopeInsertionSettings;

    const definitionType: TrussDefinitionType = options?.featurePatternContainerGuid
      ? 'FeaturePattern'
      : options?.roofContainerGuid
        ? 'Profile'
        : getDefinitionTypeForComponentType(
            insertionSettings.lastComponentType,
            insertionSettings.lastRoofTrussDefinitionType,
          );

    const componentType: TrussType = getComponentTypeForDefinitionType(
      definitionType,
      insertionSettings.lastComponentType,
    );

    const featurePatternContainerGuid = options?.featurePatternContainerGuid ?? null;
    let roofContainerGuid: string | null = null;

    if (definitionType === 'Profile') {
      roofContainerGuid = (() => {
        if (options?.roofContainerGuid) return options.roofContainerGuid;
        if (selectedEntry?.type === 'roofContainer') return selectedEntry.item.guid;
        if (selectedEntry?.type === 'trussEnvelope') return selectedEntry.item.roofContainerGuid;

        return [...getVisibleRoofContainerGuids(state)][0] ?? null;
      })();

      if (roofContainerGuid) {
        const roofContainerIsSelected =
          selectedEntry?.type === 'roofContainer' && selectedEntry.item.guid === roofContainerGuid;

        if (!roofContainerIsSelected) {
          dispatch(selectItemByGuid(roofContainerGuid, 'roofContainer'));
        }
      }
    }

    const levelGuid = options?.levelGuid
      ? options.levelGuid
      : roofContainerGuid
        ? state.layout.roofContainers[roofContainerGuid].levelGuid
        : getActiveLayoutLevelGuidOrNull(state);
    if (levelGuid) dispatch(ensureLevelIsVisible(levelGuid, { exclusive: true }));

    dispatch(
      applicationOnInsertingTrussEnvelope({
        roofContainerGuid,
        featurePatternContainerGuid,
        componentType,
        definitionType,
        levelGuid,
        ...insertionSettings.defaultProperties[definitionType],
      }),
    );
  };

export const changeInsertionTrussEnvelopeDefinitionType =
  (definitionType: TrussDefinitionType, extraChanges?: Partial<Omit<TrussEnvelope, 'guid'>>): ThunkActionVoid =>
  (dispatch, getState) => {
    const defaultProperties = getState().layout.trussEnvelopeInsertionSettings.defaultProperties[definitionType];

    dispatch(
      setInsertionTrussEnvelopeProperties({
        definitionType,
        ...defaultProperties,
        ...(definitionType === 'Profile' ? { featurePatternContainerGuid: null } : { roofContainerGuid: null }),
        ...extraChanges,
      }),
    );
  };

export const changeInsertionTrussEnvelopeComponentType =
  (componentType: TrussType): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    const insertion = getTrussEnvelopeInsertionInformation(state);

    if (insertion == null || componentType === insertion.data.componentType) return;

    const definitionType = getDefinitionTypeForComponentType(
      componentType,
      state.layout.trussEnvelopeInsertionSettings.lastRoofTrussDefinitionType,
    );

    if (definitionType === insertion.data.definitionType) {
      dispatch(setInsertionTrussEnvelopeProperties({ componentType }));
    } else {
      dispatch(changeInsertionTrussEnvelopeDefinitionType(definitionType, { componentType }));
    }
  };

export const insertAndRepeatTrussEnvelopeIfFullyDefined = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const insertion = getTrussEnvelopeInsertionInformation(getState());
  if (insertion?.isInsertionEnvelopeFullyDefined()) {
    await dispatch(finishInsertingTrussEnvelope({ postInsertionAction: 'repeat' }));
  }
};

export const createTrussEnvelopes =
  (envelopes: TrussEnvelope[]): AsyncThunkActionReturn<{ success: boolean }> =>
  async (dispatch, getState) => {
    const state = getState();
    if (state.application.openContext?.type !== 'project') {
      throw new Error('Truss envelopes (like all other layout models) can only exist within a project.');
    }

    const spinnerId = dispatch(addSpinnerMessage('Saving truss envelopes...'));

    let syncPayload;

    try {
      syncPayload = await designService.projects.upsertTrussEnvelopes(
        state.application.openContext.projectGuid,
        envelopes,
      );
    } catch (error) {
      console.error(error);
      dispatch(
        enqueueError(
          getUserVisibleOrDefaultExceptionMessage(
            error,
            'Unable to save truss envelopes. Please try again or contact Paragon Customer Support.',
          ),
        ),
      );
      return { success: false };
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }

    dispatch(layoutSlice.actions.trussEnvelopesUpserted(envelopes));
    dispatch(receiveActiveProjectSynchronizationPayload(syncPayload));
    return { success: true };
  };

export const finishInsertingTrussEnvelope =
  (options?: { postInsertionAction?: 'repeat' | 'start-run' }): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const insertion = getTrussEnvelopeInsertionInformation(state);

    if (insertion == null) {
      throw new Error("Envelope creation is not ongoing. Insertion should have been prevented by user interface.'");
    }

    if (!insertion.isInsertionEnvelopeFullyDefined()) {
      throw new Error('Truss envelope is incomplete. Insertion should have been prevented by user interface.');
    }

    const name = findNextName(getAllTrussEnvelopeNames(state));
    const overrideProperties: Partial<TrussEnvelope> =
      insertion.data.featurePatternContainerGuid !== null ? DEFAULT_FEATURE_PATTERN_TRUSS_ADDITIONAL_CUTS : {};
    const trussEnvelope = { ...insertion.data, name, ...overrideProperties };

    const { success } = await dispatch(createTrussEnvelopes([trussEnvelope]));

    if (success) {
      switch (options?.postInsertionAction) {
        case 'repeat':
          dispatch(resetInsertionTrussEnvelope());
          return;

        case 'start-run':
          dispatch(startInsertingTrussEnvelopeRun(trussEnvelope.guid));
          return;
      }
    }

    dispatch(clearInsertingObject());
    if (success) dispatch(selectItem(trussEnvelope, 'trussEnvelope'));
  };

export const startInsertingTrussEnvelopeRun = (baseEnvelopeGuid: string) =>
  applicationOnInsertingTrussEnvelopeRun({ baseEnvelopeGuid });

export const startInsertingCornerSet =
  (girderGuid: string, type: CornerSetType): ThunkActionVoid =>
  (dispatch) => {
    dispatch(applicationOnInsertingCornerSet({ girderGuid }));
    dispatch(updateInsertionCornerSet({ type })); // Also sets the picking mode
  };

export const updateInsertionCornerSet =
  (newData: Partial<InsertingCornerSetInfo['data']>): ThunkActionVoid =>
  (dispatch, getState) => {
    console.log({ newData });
    dispatch(setInsertionCornerSetData(newData));

    const { insertingObjectInfo } = getState().application;
    if (insertingObjectInfo?.type !== 'cornerSet') throw new Error('Expected to be inserting corner set');
    const { plane1Guid, plane2Guid } = insertingObjectInfo.data;

    if (plane1Guid == null) {
      dispatch(applicationOnPickingPlane({ type: 'corner-set-plane-1' }));
    } else if (plane2Guid == null) {
      dispatch(applicationOnPickingPlane({ type: 'corner-set-plane-2' }));
    } else {
      dispatch(finishInsertingCornerSet());
    }
  };

export const finishInsertingCornerSet = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const { insertingObjectInfo } = getState().application;
  if (insertingObjectInfo?.type !== 'cornerSet') throw new Error('Expected to be inserting corner set.');

  const { type, spacing, plane1Guid, plane2Guid, girderGuid, minJackLength } = insertingObjectInfo.data;
  if (plane1Guid == null || plane2Guid == null) throw new Error('Expected corner set to be fully defined.');

  await dispatch(insertCornerSet(type, plane1Guid, plane2Guid, girderGuid, spacing, minJackLength));

  dispatch(clearInsertingObject());
  dispatch(resetView());
};

export const insertCornerSet =
  (
    type: CornerSetType,
    plane1Guid: string,
    plane2Guid: string,
    girderGuid: string,
    spacing: number,
    minJackLength: number,
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const showError = (error?: string) => {
      const messageAddition = error ? `: ${error}.` : '.';
      dispatch(enqueueError(`Error creating corner set${messageAddition}`));
    };

    // From base segments of planes, find intersection (or quit)
    const planeGeometries = getCalculatedRoofAndCeilingPlanes(getState());
    const plane1Geometry = planeGeometries[plane1Guid];
    const plane2Geometry = planeGeometries[plane2Guid];
    if (!plane1Geometry || !plane2Geometry) {
      showError();
      return;
    }

    const outerCorner = plane1Geometry.calculatedGeometry.line.intersectWithLine(
      plane2Geometry.calculatedGeometry.line,
    );
    if (!outerCorner) {
      showError('Planes do not form a corner');
      return;
    }

    // From planes, find ridgeline (or quit)
    const plane1AsPlane = convertPlaneToGeometricPlane(plane1Geometry, 'Bottom');
    const plane2AsPlane = convertPlaneToGeometricPlane(plane2Geometry, 'Bottom');
    const sharedRidgeline = plane1AsPlane.intersectWithPlane(plane2AsPlane)?.projectTo2D();
    if (!sharedRidgeline) {
      showError('Planes do not form a corner');
      return;
    }

    // Find intersections of ridgeline and truss envelope, then find intersection nearest base segment intersection (or quit)
    const girder = getState().layout.trussEnvelopes[girderGuid];
    const girderFrontSegment = getEnvelopeSegmentFromEnvelope(girder, 'Front');
    const girderBackSegment = getEnvelopeSegmentFromEnvelope(girder, 'Back');
    if (!girderFrontSegment || !girderBackSegment) {
      showError('Girder does not have a shape');
      return;
    }

    const innerCorner = minBy(
      [girderFrontSegment, girderBackSegment]
        .map((segment) => segment.intersectWithLine(sharedRidgeline))
        .filter(notNullish),
      (point) => point.distanceTo(outerCorner),
    );
    if (!innerCorner) {
      showError("Girder does not intersect the corner's ridgeline");
      return;
    }

    // Find heel side of KJ segment
    const kingJackHeelPoint = minBy(
      [plane1Geometry, plane2Geometry]
        .map(({ calculatedGeometry }) => calculatedGeometry.line.intersectWithLine(sharedRidgeline))
        .filter(notNullish),
      (intersection) => intersection.distanceTo(innerCorner),
    );
    if (!kingJackHeelPoint) {
      showError('Could not determine king jack placement');
      return;
    }

    // Form KJ segment
    if (innerCorner.equals(kingJackHeelPoint)) {
      showError('No space to make a corner set');
      return;
    }

    const kingJackSegment = new LineSegment2D(kingJackHeelPoint, innerCorner); // Orientation matters here: Clearspan likes jack heels to be on the left side of the truss.

    // Find intersections of girder and plane segments, then find intersection which projects onto KJ segment (or quit)
    const girderFaceLine = new Line2D(innerCorner, girderFrontSegment.direction);
    const girderPlaneIntersection = [plane1Geometry, plane2Geometry]
      .map(({ calculatedGeometry }) => calculatedGeometry.line.intersectWithLine(girderFaceLine))
      .filter(notNullish)
      .find((intersection) => intersection.projectOntoLineSegment(kingJackSegment) != null);
    if (!girderPlaneIntersection || kingJackSegment.contains(girderPlaneIntersection)) {
      // This happens when the girder is oriented perpendicular or parallel to the ridgeline.
      showError('Could not determine corner shape');
      return;
    }

    // Find other side point of the corner, which will have the same interior angle as girderPlaneIntersection
    const girderPlaneIntersectionInteriorAngle = Direction2D.fromPoints(
      girderPlaneIntersection,
      kingJackSegment.endPoint,
    ).angleTo(Direction2D.fromPoints(girderPlaneIntersection, kingJackSegment.basePoint));
    const otherSideOuterEdgeAsLine = [plane1Geometry, plane2Geometry].find(
      ({ calculatedGeometry }) => !calculatedGeometry.line.containsPoint(girderPlaneIntersection),
    )?.calculatedGeometry.line;
    if (!otherSideOuterEdgeAsLine) {
      showError('Could not determine corner shape');
      return;
    }
    const otherSideInnerEdgeDirection = otherSideOuterEdgeAsLine.direction.rotate(girderPlaneIntersectionInteriorAngle);
    const otherSideInnerEdgeAsLine = new Line2D(kingJackSegment.endPoint, otherSideInnerEdgeDirection);
    const otherSidePoint = otherSideInnerEdgeAsLine.intersectWithLine(otherSideOuterEdgeAsLine);
    if (!otherSidePoint) {
      showError('Could not determine corner shape');
      return;
    }

    const pattern: TrussEnvelope = {
      ...girder,
      numberOfPlies: 1, // The girder may be multi-ply, but jacks should be single ply.
      justification: 'Center', // King jacks are centered on the ridgeline; other jacks can override this.
      componentDesignGuid: null, // Jacks should not reuse the girder's design.
      orderedBottomCuts: DEFAULT_ORDERED_CUTS, // Ordered cuts on the girder are likely irrelevant to jacks.
      orderedTopCuts: DEFAULT_ORDERED_CUTS, // Ordered cuts on the girder are likely irrelevant to jacks.
    };

    const makeSpacedPoints = (
      segment: LineSegment2D,
      spacingDirection: Direction2D,
      startOffset: number,
      endOffset: number,
    ) => {
      const spacingCoefficient = 1 / segment.direction.dotProduct(spacingDirection);
      const result = [];
      for (let i = startOffset; i <= segment.length / spacingCoefficient - endOffset; i += spacing) {
        result.push(segment.getPointAt(i * spacingCoefficient)!);
      }
      return result;
    };

    const makeHalfCornerSet = (
      sidePoint: Point2D,
      insideSegment: LineSegment2D,
      options: { type: CornerSetType; includeFirst?: boolean },
    ): { segment: LineSegment2D; pattern: InsertingTrussEnvelopeData }[] => {
      // Determine whether side points are left or right (from vantage of girder towards corner)
      const leftOfCorner = kingJackSegment.direction.rotateReverseQuarterTurn();
      const isHeelLeftOfCorner =
        sidePoint.getCoordinate(leftOfCorner) > kingJackSegment.midpoint.getCoordinate(leftOfCorner);
      const justification: EnvelopeJustification = isHeelLeftOfCorner ? 'Back' : 'Front';

      const sideCornerEdge = new LineSegment2D(sidePoint, outerCorner);
      const spacingDirection = new LineSegment2D(sidePoint, innerCorner).direction
        .rotateQuarterTurn()
        .orientWith(sideCornerEdge.direction);

      const startOffset = options.includeFirst ? 0 : spacing;
      const endOffset = options.type === 'Atlantic' ? minJackLength : 0;
      const sideCornerEdgePoints = makeSpacedPoints(sideCornerEdge, spacingDirection, startOffset, endOffset);
      const insideSegmentPoints = makeSpacedPoints(insideSegment.reverse(), spacingDirection, startOffset, endOffset);

      return zip(sideCornerEdgePoints, insideSegmentPoints)
        .map(([sideEdgePoint, insidePoint]) => {
          if (!sideEdgePoint || !insidePoint) return null;

          if (distanceLessThan(sideEdgePoint.distanceTo(insidePoint), minJackLength)) return null;

          // Orientation matters here: Clearspan likes jack heels to be on the left side of the truss.
          return { segment: new LineSegment2D(sideEdgePoint, insidePoint), pattern: { ...pattern, justification } };
        })
        .filter(notNullish);
    };

    switch (type) {
      case 'Stepdown':
        const side1Segments = makeHalfCornerSet(otherSidePoint, kingJackSegment, { type, includeFirst: true });
        const side2Segments = makeHalfCornerSet(girderPlaneIntersection, kingJackSegment, { type });
        const cornerJackSegments = side1Segments.concat(side2Segments);

        // Insert KJ, then CJs
        await dispatch(insertEnvelopeFromSegments([{ segment: kingJackSegment, pattern }]));
        await dispatch(insertEnvelopeFromSegments(cornerJackSegments));
        break;

      case 'Atlantic':
        const girderSegment = new LineSegment2D(girderPlaneIntersection, innerCorner);
        const longJackSegments = makeHalfCornerSet(otherSidePoint, girderSegment, { type, includeFirst: true });
        if (longJackSegments.length === 0) break;
        const subgirderSegment = longJackSegments.at(-1)!.segment;
        const shortJackSegments = makeHalfCornerSet(girderPlaneIntersection, subgirderSegment, { type });

        // Insert long jacks, then short jacks
        await dispatch(insertEnvelopeFromSegments(longJackSegments));
        await dispatch(insertEnvelopeFromSegments(shortJackSegments));
        break;

      default:
        assertExhaustiveSwitch(type);
    }
  };

export function topOrBottomCutEquals(a: TopOrBottomCut, b: TopOrBottomCut) {
  if (a.type === 'Absolute' && b.type === 'Absolute') {
    return Plane3D.fromObject(a.plane).equals(Plane3D.fromObject(b.plane));
  }

  if (a.type === 'RoofPlane' && b.type === 'RoofPlane') {
    return a.roofPlaneGuid === b.roofPlaneGuid;
  }

  if (a.type === 'CeilingPlane' && b.type === 'CeilingPlane') {
    return a.ceilingPlaneGuid === b.ceilingPlaneGuid;
  }

  return false;
}

export function getTargetedTrussEnvelopes(
  targetInfo: NonInsertionTrussEnvelopeTargetInfo,
  trussEnvelopes: ReadonlyRecord<string, TrussEnvelope>,
): readonly TrussEnvelope[] {
  switch (targetInfo.target) {
    case 'existingTrussEnvelope':
      return [trussEnvelopes[targetInfo.trussEnvelopeGuid]];

    case 'existingTrussEnvelopes':
      return targetInfo.trussEnvelopeGuids.map((guid) => trussEnvelopes[guid]);

    default:
      assertExhaustiveSwitch(targetInfo);
  }
}

export const deleteTrussEnvelopeCut =
  (
    targetInfo: TrussEnvelopeTargetInfo,
    cutOrientation: 'top' | 'bottom',
    cutToRemove: TopOrBottomCut,
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const cutsProperty = cutOrientation === 'top' ? 'topCuts' : 'bottomCuts';

    if (targetInfo.target === 'insertionTrussEnvelope') {
      if (state.application.insertingObjectInfo?.type !== 'trussEnvelope') throw new Error('Invalid insertion state.');

      const existingCuts = state.application.insertingObjectInfo.data[cutsProperty];

      dispatch(
        setInsertionTrussEnvelopeProperties({
          [cutsProperty]: existingCuts.filter((cut) => !topOrBottomCutEquals(cutToRemove, cut)),
        }),
      );

      return;
    }

    const trussEnvelopes = getTargetedTrussEnvelopes(targetInfo, state.layout.trussEnvelopes);

    await dispatch(
      editExistingTrussEnvelopes(
        trussEnvelopes.map((envelope) => ({
          guid: envelope.guid,
          editedProperties: {
            [cutsProperty]: envelope[cutsProperty].filter((cut) => !topOrBottomCutEquals(cut, cutToRemove)),
          },
        })),
      ),
    );
  };

export const deleteTrussEnvelopeOrderedCuts =
  (targetInfo: TrussEnvelopeTargetInfo, cutOrientation: 'top' | 'bottom'): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const cutsProperty = cutOrientation === 'top' ? 'orderedTopCuts' : 'orderedBottomCuts';

    if (targetInfo.target === 'insertionTrussEnvelope')
      throw new Error('Editing ordered plane cuts during insertion is not supported');

    const trussEnvelopes = getTargetedTrussEnvelopes(targetInfo, state.layout.trussEnvelopes);

    await dispatch(
      editExistingTrussEnvelopes(
        trussEnvelopes.map((envelope) => ({
          guid: envelope.guid,
          editedProperties: { [cutsProperty]: { ...envelope[cutsProperty], cuts: [] } },
        })),
      ),
    );
  };

export const beginTrussEnvelopeCutPicking =
  (targetInfo: TrussEnvelopeTargetInfo, cutOrientation: 'top' | 'bottom', ordered: boolean): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();

    if (isLayoutBasedOnRoofContainers(state)) {
      dispatch(applicationOnPickingRoofFace({ ordered, cutOrientation, targetInfo }));
      return;
    }

    dispatch(
      applicationOnPickingPlane({
        type: `truss-envelope${ordered ? '-ordered' : ''}-${cutOrientation}-cut`,
        trussEnvelopeTargetInfo: targetInfo,
        hasRoofPlanes: getVisibleRoofPlaneCount(state) > 0,
        hasCeilingPlanes: getVisibleCeilingPlaneCount(state) > 0,
      }),
    );
  };

export const finishCuttingEnvelope = () => clearPickingObject();

export const setTrussEnvelopeOrderedCutsReversal =
  (targetInfo: TrussEnvelopeTargetInfo, cutOrientation: 'top' | 'bottom', reversed: boolean): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();

    if (targetInfo.target === 'insertionTrussEnvelope')
      throw new Error('Editing ordered plane cuts during insertion is not supported');

    const trussEnvelopes = getTargetedTrussEnvelopes(targetInfo, state.layout.trussEnvelopes);

    const cutsProperty = cutOrientation === 'top' ? 'orderedTopCuts' : 'orderedBottomCuts';
    await dispatch(
      editExistingTrussEnvelopes(
        trussEnvelopes.map((envelope) => ({
          guid: envelope.guid,
          editedProperties: { [cutsProperty]: { ...envelope[cutsProperty], reversed } },
        })),
      ),
    );
  };

export const editCapSet =
  (selectedEnvelopes: readonly TrussEnvelope[], capSetState: CapSetState): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const allTrussEnvelopes = state.layout.trussEnvelopes;
    const capSetGroups = calculateTrussEnvelopeCapSetGroupings(state);
    const ecosystem = getGeometricEcosystemForActivePortionOfLayout(state);

    // Find all affected envelopes (not just selected ones) in groups
    const groupsToOperateOn = uniq(selectedEnvelopes.map(({ guid }) => capSetGroups[guid].involvedEnvelopeGuids)).map(
      (groupOfGuids) => groupOfGuids.map((guid) => allTrussEnvelopes[guid]),
    );

    // Build list of envelope cap properties to apply to each envelope
    const capSetProperties = generateCapSetProperties(capSetState);

    const createdEnvelopes = groupsToOperateOn.flatMap((group) => {
      // For each group, find representative TrussEnvelope properties from the longest truss envelope
      const representativeEnvelope = maxBy(group, (envelope) =>
        point2DDistanceTo(envelope.leftPoint, envelope.rightPoint),
      )!;
      const envelopeDefinitionLine = getEnvelopeSegment(representativeEnvelope)?.toLine();
      if (!envelopeDefinitionLine) {
        throw new Error('Expected representativeEnvelope to have a definition line');
      }

      // Re-create each group by combining envelope cap properties list and representative properties to create a list
      // of envelopes...
      const newEnvelopes = capSetProperties
        .map(({ properties: capLevelProperties, capIndex }) => {
          const nameOverride = capIndex === 0 ? {} : { name: `${representativeEnvelope.name}-Cap${capIndex}` };
          const orderedCutsOverride =
            capIndex === 0
              ? {}
              : { orderedTopCuts: { cuts: [], reversed: false }, orderedBottomCuts: { cuts: [], reversed: false } };
          const unadjustedEnvelope: TrussEnvelope = {
            ...representativeEnvelope,
            ...capLevelProperties,
            ...nameOverride,
            ...orderedCutsOverride,
            componentDesignGuid: null,
            guid: crypto.randomUUID(),
          };

          if (unadjustedEnvelope.containerLevel === 'NotCapped' || unadjustedEnvelope.containerLevel === 'BaseLevel')
            return unadjustedEnvelope;

          // ...adjusting the left/right points of cap-level envelopes as needed
          return (
            StageOneTrussEnvelope.fromPoints(unadjustedEnvelope.leftPoint, unadjustedEnvelope.rightPoint, { ecosystem })
              ?.boundIntoSegmentAroundDefiningPoint(unadjustedEnvelope)
              ?.adjustToAccountForBevels('PreferNone')
              ?.getResult()
              .applyTo(unadjustedEnvelope) ?? null
          );
        })
        .filter(notNullish);
      return newEnvelopes;
    });

    // Delete old envelopes
    const trussEnvelopesToRemove = groupsToOperateOn
      .flat()
      .map((envelope): SelectableItemEntry => ({ type: 'trussEnvelope', item: envelope }));
    await dispatch(deleteItems(trussEnvelopesToRemove));

    // Insert and select all resulting envelopes
    if (createdEnvelopes.length > 0) {
      await dispatch(saveTrussEnvelopes(createdEnvelopes));
      dispatch(selectItems(createdEnvelopes, 'trussEnvelope'));
    }
  };

export type UserSpecifiedBevelCutChanges = {
  leftBevelCut?: UserSpecifiedBevelCut;
  rightBevelCut?: UserSpecifiedBevelCut;
};

const recalculateTrussEnvelope =
  (
    envelope: FullyDefinedInsertingTrussEnvelopeData,
    bevelCuts?: UserSpecifiedBevelCutChanges,
  ): ThunkActionReturn<EnvelopeCalculationResult | { error: string }> =>
  (_, getState) => {
    const state = getState();
    const preferredBevelCutType =
      state.userSettings?.layoutSettings.bevelCutTypePreference ?? DEFAULT_BEVEL_CUT_TYPE_PREFERENCE;

    const dependencies = getTrussSolidCalculationDependencies(state);

    const ecosystem = envelope.roofContainerGuid
      ? getGeometricEcosystemScopedToSingleRoofContainer(state, envelope.roofContainerGuid, envelope.guid, dependencies)
      : getGeometricEcosystemForActivePortionOfLayout(state);

    if (ecosystem == null) return { error: 'data needed to calculate could not be found.' };

    const result = StageOneTrussEnvelope.forExistingEnvelope(envelope, ecosystem)
      ?.boundIntoSegmentAroundDefiningPoint(envelope)
      ?.adjustToAccountForBevels(preferredBevelCutType, {
        baseBevelCut: bevelCuts?.leftBevelCut,
        endBevelCut: bevelCuts?.rightBevelCut,
      })
      ?.getResult();

    return result ?? { error: 'calculation of envelope failed.' };
  };

const insertEnvelopeFromSegments =
  (
    segmentsWithPatterns: readonly { segment: LineSegment2D; pattern: InsertingTrussEnvelopeData }[],
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const preferredBevelCutType =
      state.userSettings?.layoutSettings.bevelCutTypePreference ?? DEFAULT_BEVEL_CUT_TYPE_PREFERENCE;

    const ecosystem = getGeometricEcosystemForActivePortionOfLayout(state);

    const takenNames = getAllTrussEnvelopeNames(state).slice();
    const newEnvelopes = [];

    for (const { segment, pattern } of segmentsWithPatterns) {
      const name = findNextName(takenNames);
      takenNames.push(name);

      const result: TrussEnvelope | undefined = StageOneTrussEnvelope.fromPoints(segment.basePoint, segment.endPoint, {
        ecosystem,
      })
        ?.boundIntoSegmentAroundDefiningPoint({ ...pattern, guid: '' }) // Don't exclude the girder from geometric calculations
        ?.adjustToAccountForBevels(preferredBevelCutType)
        ?.getResult()
        .toTrussEnvelope(name, { ...pattern, guid: crypto.randomUUID() });

      if (result) newEnvelopes.push(result);
    }

    await dispatch(saveTrussEnvelopes(newEnvelopes));
  };

const applyUserSpecifiedBevelCut = (
  bevelCut: TrussEnvelopeBevelCut | null,
  userSpecifiedCut: UserSpecifiedBevelCut | undefined,
): TrussEnvelopeBevelCut | null => {
  if (userSpecifiedCut === null) return null;
  if (userSpecifiedCut === undefined) return bevelCut;
  if (userSpecifiedCut.angle !== undefined) return { ...userSpecifiedCut, angle: userSpecifiedCut.angle };
  return { type: userSpecifiedCut.type, angle: bevelCut?.angle ?? 45 };
};

const calculateBevelCutExtensionCoefficient = (bevelCut: TrussEnvelopeBevelCut | null) => {
  if (!bevelCut) return 0;
  const tanAngle = Math.tan(convertDegreesToRadians(bevelCut.angle));
  return bevelCut.type === 'Double' ? tanAngle / 2 : tanAngle;
};

const calculateEnvelopeDefinitionPointShift = (
  oldBevelCut: TrussEnvelopeBevelCut | null,
  newBevelCut: TrussEnvelopeBevelCut | null,
  totalEnvelopeThickness: number,
) =>
  isEqual(oldBevelCut, newBevelCut)
    ? 0
    : (calculateBevelCutExtensionCoefficient(newBevelCut) - calculateBevelCutExtensionCoefficient(oldBevelCut)) *
      totalEnvelopeThickness;

const shiftPointAway = (pointToShift: Point2DInterface, referencePoint: Point2DInterface, shiftDistance: number) =>
  Line2D.fromPointObjects(pointToShift, referencePoint).getPointAlongLine(-shiftDistance).toObject();

const editTrussEnvelopeToAccommodateBevelCutChange = <T extends Omit<TrussEnvelope, 'name'>>(
  trussEnvelope: T,
  bevelCuts: UserSpecifiedBevelCutChanges | undefined,
): T => {
  if (!bevelCuts) return trussEnvelope;

  const { leftBevelCut, rightBevelCut, leftPoint, rightPoint } = trussEnvelope;
  const newLeftBevelCut = applyUserSpecifiedBevelCut(leftBevelCut, bevelCuts.leftBevelCut);
  const newRightBevelCut = applyUserSpecifiedBevelCut(rightBevelCut, bevelCuts.rightBevelCut);

  const totalThickness = trussEnvelope.thickness * trussEnvelope.numberOfPlies;
  const leftShift = calculateEnvelopeDefinitionPointShift(leftBevelCut, newLeftBevelCut, totalThickness);
  const rightShift = calculateEnvelopeDefinitionPointShift(rightBevelCut, newRightBevelCut, totalThickness);

  return {
    ...trussEnvelope,
    leftPoint: shiftPointAway(leftPoint, rightPoint, leftShift),
    rightPoint: shiftPointAway(rightPoint, leftPoint, rightShift),
    leftBevelCut: newLeftBevelCut,
    rightBevelCut: newRightBevelCut,
  };
};

export const recalculateExistingTrussEnvelopes =
  (
    envelopes: TrussEnvelope | TrussEnvelope[],
    options: { end?: EnvelopeRecalculationEnd } = {},
  ): AsyncThunkActionVoid =>
  async (dispatch) => {
    const { end = 'both' } = options;

    const failedEnvelopes = [];
    const recalculatedEnvelopes = [];

    for (const envelope of forceArray(envelopes)) {
      const result = dispatch(recalculateTrussEnvelope(envelope, undefined));
      if (result instanceof EnvelopeCalculationResult) {
        recalculatedEnvelopes.push(result.applyTo(envelope, end));
      } else {
        failedEnvelopes.push(envelope);
      }
    }

    if (recalculatedEnvelopes.length > 0) {
      await dispatch(saveTrussEnvelopes(recalculatedEnvelopes));
    }

    if (failedEnvelopes.length !== 0) {
      dispatch(enqueueWarning('Some truss envelopes could not be calculated at this time and were skipped.'));
    }
  };

interface EnvelopeEditInstructions {
  guid: string;

  editedProperties?: Partial<Omit<TrussEnvelope, 'guid' | 'leftBevelCut' | 'rightBevelCut'>>;
  bevelCuts?: UserSpecifiedBevelCutChanges;
}

export const editExistingTrussEnvelopes =
  (changes: EnvelopeEditInstructions | EnvelopeEditInstructions[]): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();

    const editedEnvelopes: TrussEnvelope[] = [];

    for (const { guid, editedProperties, bevelCuts } of forceArray(changes)) {
      const trussEnvelope = state.layout.trussEnvelopes[guid];
      if (trussEnvelope == null) throw new Error('Unable to find truss envelope when editing.');

      let editedEnvelope = { ...trussEnvelope, ...editedProperties };

      if (bevelCuts !== undefined) {
        editedEnvelope = editTrussEnvelopeToAccommodateBevelCutChange(editedEnvelope, bevelCuts);
      }

      editedEnvelopes.push(editedEnvelope);
    }

    if (editedEnvelopes.length > 0) {
      await dispatch(saveTrussEnvelopes(editedEnvelopes));
    }
  };

export const flipTrussEnvelopes =
  (envelopes: TrussEnvelope | readonly TrussEnvelope[]): ThunkActionVoid =>
  (dispatch) => {
    const envelopesArray = forceArray(envelopes);
    const flippedEnvelopes = envelopesArray.map(flipTrussEnvelope);

    if (flippedEnvelopes.some((e) => e.componentDesignGuid != null)) {
      dispatch(showFlipTrussEnvelopeDialog({ flippedEnvelopes }));
    } else {
      dispatch(saveTrussEnvelopes(flippedEnvelopes));
    }
  };

export const editInsertionTrussEnvelope =
  (
    editedProperties?: Partial<Omit<TrussEnvelope, 'guid'>>,
    bevelCuts?: UserSpecifiedBevelCutChanges,
  ): ThunkActionVoid =>
  (dispatch, getState) => {
    const insertion = getTrussEnvelopeInsertionInformation(getState());
    if (insertion == null) throw new Error('Insertion was not occurring when recalculating insertion envelope.');

    const editedInsertionEnvelope = { ...insertion.data, ...editedProperties };

    if (
      insertion.activeMode === 'point-selection' ||
      !insertion.isArbitraryTrussEnvelopeDataFullyDefined(editedInsertionEnvelope)
    ) {
      const leftBevelCut = applyUserSpecifiedBevelCut(editedInsertionEnvelope.leftBevelCut, bevelCuts?.leftBevelCut);
      const rightBevelCut = applyUserSpecifiedBevelCut(editedInsertionEnvelope.rightBevelCut, bevelCuts?.rightBevelCut);

      const { leftOverhang = 0, rightOverhang = 0, elevation = 0 } = insertion.data;

      dispatch(
        setInsertionTrussEnvelopeProperties({
          ...editedProperties,
          leftBevelCut,
          rightBevelCut,
          leftOverhang,
          rightOverhang,
          elevation,
        }),
      );

      return;
    }

    if (editedProperties) {
      dispatch(
        setInsertionTrussEnvelopeProperties(
          editTrussEnvelopeToAccommodateBevelCutChange(editedInsertionEnvelope, bevelCuts),
        ),
      );
    }
  };

export const recalculateInsertionTrussEnvelope =
  (end: EnvelopeRecalculationEnd = 'both'): ThunkActionVoid =>
  (dispatch, getState) => {
    const insertion = getTrussEnvelopeInsertionInformation(getState());
    if (insertion == null || !insertion.isInsertionEnvelopeFullyDefined()) return;

    const result = dispatch(recalculateTrussEnvelope(insertion.data));
    if (result instanceof EnvelopeCalculationResult) {
      dispatch(setInsertionTrussEnvelopeProperties(result.applyTo(insertion.data, end)));
    } else {
      console.error(`Unable to recalculate insertion truss envelope—${result.error}`);
      dispatch(
        enqueueError('Unable to recalculate truss envelope. Please try again or contact Paragon Customer Support.'),
      );
    }
  };

export const saveTrussEnvelopes =
  (envelopes: TrussEnvelope | readonly TrussEnvelope[]): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { openContext } = getState().application;
    if (openContext?.type !== 'project') {
      throw new Error('Unable to save truss envelopes without an open project.');
    }

    const envelopesArray = forceArray(envelopes);
    if (envelopesArray.length === 0) return;

    const envelopeWord = envelopesArray.length > 1 ? 'envelopes' : 'envelope';

    let syncPayload;

    const spinnerId = dispatch(addSpinnerMessage(`Saving truss ${envelopeWord}...`));

    try {
      syncPayload = await designService.projects.upsertTrussEnvelopes(openContext.projectGuid, envelopesArray);
    } catch (error) {
      console.error(error);
      dispatch(
        enqueueError(
          getUserVisibleOrDefaultExceptionMessage(
            error,
            `Unable to save truss ${envelopeWord}. Please try again, or contact Paragon Customer Support.`,
          ),
        ),
      );

      return;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }

    dispatch(layoutSlice.actions.trussEnvelopesUpserted(envelopesArray));
    dispatch(receiveActiveProjectSynchronizationPayload(syncPayload));
  };

const ENVELOPE_DESIGNING_USER_VISIBLE_ERROR =
  'Unable to create component designs for truss envelopes. Try again or contact Paragon Customer Support';

const getComponentPlaneIntersectionWarningMessage = (envelopeNames: readonly string[]) =>
  `The ${pluralize('component', envelopeNames.length)} associated with truss ${pluralize(
    'envelope',
    envelopeNames.length,
  )} ${formatAsNaturalLanguageList(envelopeNames.toSorted(naturalSort), 'and')} may intersect roof or ceiling planes.`;

const bulkDesignTrussEnvelopes =
  (
    trussEnvelopes: TrussEnvelope[],
    spinnerMessage: string,
    nameSuggestions: ReadonlyRecord<string, string> | null = null,
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const { openContext } = state.application;

    if (openContext?.type !== 'project') {
      console.error('Unable to design truss envelopes without an open project.');
      dispatch(enqueueError(ENVELOPE_DESIGNING_USER_VISIBLE_ERROR));

      return;
    }

    const spinnerId = dispatch(addSpinnerMessage(spinnerMessage));

    try {
      const response = await designService.projects.createTrusses(
        openContext.projectGuid,
        trussEnvelopes,
        nameSuggestions,
      );

      if (response.trussEnvelopeGuidsToWarnIntersectingPlanes.length) {
        const envelopeNamesToWarn = response.trussEnvelopeGuidsToWarnIntersectingPlanes.map(
          (guid) => state.layout.trussEnvelopes[guid].name,
        );
        dispatch(enqueueWarning(getComponentPlaneIntersectionWarningMessage(envelopeNamesToWarn)));
      }

      for (const componentDesign of response.createdComponentDesigns) {
        await dispatch(
          receiveComponentDesign(componentDesign, {
            commitComponentDesign: true,
            replaceWorkingChanges: true,
            isPersisted: true,
            // We'll add the jack-girder connections all at once afterward to avoid having temporary invalid states
            // (jack-girder connections referencing component designs before they're added to the Redux store).
            skipJackGirderConnections: true,
          }),
        );
      }

      dispatch(setJackGirderConnections(response.jackGirderConnections));
      dispatch(layoutSlice.actions.trussEnvelopesDesigned(response));
      dispatch(receiveActiveProjectSynchronizationPayload(response.synchronizationPayload));
    } catch (error) {
      console.error(error);
      dispatch(enqueueError(ENVELOPE_DESIGNING_USER_VISIBLE_ERROR));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const designAllTrussEnvelopes = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const trussEnvelopes = Object.values(getState().layout.trussEnvelopes);
  const unDesignedTrussEnvelopes = trussEnvelopes.filter((envelope) => envelope.componentDesignGuid == null);

  await dispatch(
    bulkDesignTrussEnvelopes(unDesignedTrussEnvelopes, 'Creating component designs for truss envelopes...'),
  );
};

const DESIGNING_SELECTED_TRUSS_ENVELOPES_MESSAGE = 'Designing selected truss envelopes...';

export const designSelectedTrussEnvelopes = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const state = getState();
  const selectedEnvelopes = getSelectedItemIdentifiers(state)
    .filter((item) => item.type === 'trussEnvelope')
    .map((item) => state.layout.trussEnvelopes[item.guid]);

  if (selectedEnvelopes.length === 1) {
    await dispatch(
      designTrussEnvelope(selectedEnvelopes[0], { matchExistingDesigns: true, jumpToTrussInDesignView: false }),
    );
    return;
  }

  await dispatch(bulkDesignTrussEnvelopes(selectedEnvelopes, DESIGNING_SELECTED_TRUSS_ENVELOPES_MESSAGE));
};

export const designTrussEnvelope =
  (
    trussEnvelope: TrussEnvelope,
    options?: { matchExistingDesigns: boolean; jumpToTrussInDesignView: boolean },
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { openContext } = getState().application;
    if (openContext?.type !== 'project') {
      console.error('Unable to design truss envelopes without an open project.');
      dispatch(enqueueError(ENVELOPE_DESIGNING_USER_VISIBLE_ERROR));

      return;
    }

    const spinnerId = dispatch(addSpinnerMessage('Creating component design for truss envelope...'));

    try {
      const response = await designService.projects.createTruss(
        openContext.projectGuid,
        trussEnvelope,
        options?.matchExistingDesigns ?? true,
      );

      if (response.type === 'Err') {
        dispatch(
          layoutSlice.actions.trussEnvelopesDesigned({
            updatedTrussEnvelopes: [],
            errors: { [trussEnvelope.guid]: response.error },
          }),
        );

        return;
      }

      const {
        updatedTrussEnvelope,
        createdComponentDesign,
        synchronizationPayload,
        jackGirderConnections,
        warnIntersectingPlanes,
      } = response;

      if (warnIntersectingPlanes)
        dispatch(enqueueWarning(getComponentPlaneIntersectionWarningMessage([trussEnvelope.name])));

      if (createdComponentDesign) {
        await dispatch(
          receiveComponentDesign(createdComponentDesign, {
            commitComponentDesign: true,
            replaceWorkingChanges: true,
            isPersisted: true,
            skipJackGirderConnections: true,
          }),
        );
      }

      dispatch(setJackGirderConnections(jackGirderConnections));
      dispatch(
        layoutSlice.actions.trussEnvelopesDesigned({
          updatedTrussEnvelopes: [updatedTrussEnvelope],
          errors: {},
        }),
      );
      dispatch(receiveActiveProjectSynchronizationPayload(synchronizationPayload));

      const componentDesign =
        updatedTrussEnvelope.componentDesignGuid != null
          ? getState().componentDesigns.data[updatedTrussEnvelope.componentDesignGuid]
          : undefined;

      if (componentDesign == null) {
        console.error('Unable to find component design that should exist.');
      } else if (options?.jumpToTrussInDesignView !== false) {
        dispatch(selectItem(componentDesign, 'componentDesign'));
      }
    } catch (error) {
      console.error(error);

      const messageEnd = isUserVisibleException(error)
        ? `: ${error.userVisibleMessage}`
        : '. Try again or contact Paragon Customer Support';
      dispatch(enqueueError(`Unable to create component design for truss envelope${messageEnd}`));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const associateTrussEnvelopesWithExistingComponentDesign =
  (trussEnvelopeGuids: string[], componentDesignGuid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const trussEnvelopes = trussEnvelopeGuids.map((guid) => state.layout.trussEnvelopes[guid]);

    if (trussEnvelopes.some((envelope) => envelope == null)) {
      throw new Error('Invalid truss envelope guid when associating truss envelope(s).');
    }

    if (!(componentDesignGuid in state.componentDesigns.data)) {
      throw new Error('Invalid component design guid when associating truss envelope.');
    }

    await dispatch(saveTrussEnvelopes(trussEnvelopes.map((envelope) => ({ ...envelope, componentDesignGuid }))));
  };

export const regenerateComponentsInEnvelopes =
  (trussEnvelopes: readonly TrussEnvelope[]): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { openContext } = getState().application;
    if (openContext?.type !== 'project') {
      throw new Error('Unable to regenerate components without an open project.');
    }

    const spinnerId = dispatch(addSpinnerMessage('Regenerating Components'));

    try {
      // 1. All truss envelopes unlinked
      const linkedComponentDesignGuids = uniq(
        trussEnvelopes.map(({ componentDesignGuid }) => componentDesignGuid).filter(notNullish),
      );

      // Names 1. Save names for reuse
      const componentNamesAndGuidsByEnvelope = trussEnvelopes
        .map(({ guid, componentDesignGuid }) => {
          if (componentDesignGuid == null) return null;
          const componentName = getState().componentDesigns.data[componentDesignGuid].component.name;
          return [guid, { componentDesignGuid, componentName }] as const;
        })
        .filter(notNullish);

      await dispatch(
        saveTrussEnvelopes(trussEnvelopes.map((trussEnvelope) => ({ ...trussEnvelope, componentDesignGuid: null }))),
      );

      // 2. Any component designs that are no longer associated with an envelope due to step 1 are deleted
      const allUsedComponentDesignGuids = new Set<string>(
        getAllTrussEnvelopes(getState())
          .map(({ componentDesignGuid }) => componentDesignGuid)
          .filter(notNullish),
      );
      const unusedComponentDesignGuids = linkedComponentDesignGuids.filter(
        (guid) => !allUsedComponentDesignGuids.has(guid),
      );

      await dispatch(deleteComponentDesignsWithoutConfirmation(unusedComponentDesignGuids));

      // Names 2. Filter to reusable names
      const namesToReuse = Object.fromEntries(
        componentNamesAndGuidsByEnvelope
          .filter(([, { componentDesignGuid }]) => unusedComponentDesignGuids.includes(componentDesignGuid))
          .map(([envelopeGuid, { componentName }]) => [envelopeGuid, componentName]),
      );

      // 3. Create or match designs run for all selected envelopes
      const emptyTrussEnvelopes = trussEnvelopes.map(({ guid }) => getState().layout.trussEnvelopes[guid]);
      await dispatch(bulkDesignTrussEnvelopes(emptyTrussEnvelopes, 'Regenerating Components', namesToReuse));

      // 4. Regenerate connections run for all envelopes affected
      const updatedTrussEnvelopes = trussEnvelopes.map(({ guid }) => getState().layout.trussEnvelopes[guid]);
      await dispatch(regenerateConnectionsOnEnvelopes(updatedTrussEnvelopes, { suppressUpdateMessage: true }));
    } catch {
      dispatch(enqueueError('Error regenerating components.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const regenerateConnectionsOnEnvelopes =
  (trussEnvelopes: readonly TrussEnvelope[], options?: { suppressUpdateMessage: boolean }): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { openContext } = getState().application;
    if (openContext?.type !== 'project') {
      throw new Error('Unable to regenerate connections without an open project.');
    }

    const spinnerId = dispatch(addSpinnerMessage('Regenerating Connections'));

    try {
      const result = await designService.projects.regenerateEnvelopeConnections(
        openContext.projectGuid,
        trussEnvelopes,
      );

      dispatch(
        setJackGirderConnections([...result.updatedJackGirderConnections, ...result.createdJackGirderConnections]),
      );
      dispatch(removeJackGirderConnections(result.deletedJackGirderConnectionGuids));
      dispatch(setComponentDesignTimestamps({ timestamps: result.timestamps }));

      if (!options?.suppressUpdateMessage) {
        const messageStart = 'Connections: ';
        const messagePiece = (array: readonly unknown[], verb: string) =>
          array.length ? `${array.length} ${verb}` : null;
        const messagePieces = [
          messagePiece(result.createdJackGirderConnections, 'added'),
          messagePiece(result.updatedJackGirderConnections, 'updated'),
          messagePiece(result.deletedJackGirderConnectionGuids, 'removed'),
        ].filter(notNullish);
        const messageMiddle = messagePieces.length ? formatAsNaturalLanguageList(messagePieces, '&') : 'No changes';

        dispatch(enqueueInfo(`${messageStart}${messageMiddle}.`));
      }
    } catch {
      dispatch(enqueueError('Error regenerating connections.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const beginRenamingDesignsAssociatedWithTrussEnvelopes =
  (trussEnvelopes: TrussEnvelope[]): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    const componentDesignGuids = uniq(trussEnvelopes.map((e) => e.componentDesignGuid))
      .filter(notNullish)
      .filter((guid) => {
        const loadedState = state.componentDesigns.loadedState[guid];
        return loadedState && loadedState.committed;
      });

    if (componentDesignGuids.length > 0) {
      dispatch(openRenameLayoutComponentsPanel({ componentDesignGuids }));
    } else {
      dispatch(enqueueWarning('No component designs can be renamed.'));
    }
  };

export const finishRenamingDesignsAssociatedWithTrussEnvelopes = (): ThunkActionVoid => (dispatch) => {
  dispatch(resetSelectedItems());
  dispatch(closeRenameLayoutComponentsPanel());
};

export const selectAllTrussEnvelopesLinkedToComponentDesign =
  (componentDesignGuid: string): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    const trussEnvelopes = Object.values(state.layout.trussEnvelopes);
    const relevantTrussEnvelopes = trussEnvelopes.filter(
      (envelope) => envelope.componentDesignGuid === componentDesignGuid,
    );

    if (relevantTrussEnvelopes.length > 0) {
      dispatch(selectItems(relevantTrussEnvelopes, 'trussEnvelope'));
    }
  };

export const regenerateAllTrussEnvelopesLinkedToComponentDesign =
  (componentDesignGuid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const trussEnvelopes = Object.values(state.layout.trussEnvelopes);
    const relevantTrussEnvelopes = trussEnvelopes.filter(
      (envelope) => envelope.componentDesignGuid === componentDesignGuid,
    );

    if (relevantTrussEnvelopes.length > 0) {
      // Save the name before the component design is removed
      const componentDesignName = state.componentDesigns.data[componentDesignGuid].component.name;

      await dispatch(regenerateComponentsInEnvelopes(relevantTrussEnvelopes));

      // Find the new component design by looking up its guid in the associated envelopes
      const regeneratedComponentDesignGuid =
        getState().layout.trussEnvelopes[relevantTrussEnvelopes[0].guid].componentDesignGuid;
      if (regeneratedComponentDesignGuid != null)
        dispatch(setComponentName(componentDesignName, regeneratedComponentDesignGuid));
    }
  };

export const startTrimOrExtendEnvelopes =
  (options?: { repeat: boolean }): ThunkActionVoid =>
  (dispatch) => {
    dispatch(
      applicationOnPickingLayoutLine({
        target: 'envelope-trim-or-extend-line',
        repeat: options?.repeat,
      }),
    );
  };

export const trimOrExtendEnvelopes =
  (
    envelopes: { trussEnvelopeGuids?: readonly string[]; bearingEnvelopeGuids?: readonly string[] },
    trimLine: Line2D,
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const trimSingleEnvelope = <T extends TrussEnvelope | BearingEnvelope>(envelope: T): T | null => {
      // Bypass zedge envelopes
      if (point2DEquals(envelope.leftPoint, envelope.rightPoint)) return null;

      const projectionLine = Line2D.fromPointObjects(envelope.leftPoint, envelope.rightPoint);

      const getDistance = (face: 'Front' | 'Back') =>
        getEnvelopeSegmentFromEnvelope(envelope, face)
          ?.toLine()
          ?.intersectWithLine(trimLine)
          ?.projectedSignedDistanceAlong(projectionLine);

      const frontDistance = getDistance('Front');
      const backDistance = getDistance('Back');
      if (frontDistance == null || backDistance == null) return null;

      const { leftOverhang = 0, rightOverhang = 0 } = isTrussEnvelope(envelope) ? envelope : {};

      const midDistance = (frontDistance + backDistance + rightOverhang - leftOverhang) / 2;
      const rightDistance = Point2D.fromObject(envelope.rightPoint).projectedSignedDistanceAlong(projectionLine);
      const endToAdjust = minBy(
        [
          { end: 'left', distance: -leftOverhang },
          { end: 'right', distance: rightDistance + rightOverhang },
        ] as const,
        ({ distance }) => Math.abs(midDistance - distance),
      )!.end;

      const trimOrExtendDistance = (endToAdjust === 'left' ? Math.max : Math.min)(frontDistance, backDistance);
      const trimOrExtendPoint = projectionLine.getPointAlongLine(trimOrExtendDistance).toObject();
      const trimmedEnvelope = produce(envelope, (draft) => {
        switch (endToAdjust) {
          case 'left':
            draft.leftPoint = trimOrExtendPoint;
            if ('leftOverhang' in draft) {
              draft.leftOverhang = 0;
              draft.leftBevelCut = null;
            }
            break;
          case 'right':
            draft.rightPoint = trimOrExtendPoint;
            if ('rightOverhang' in draft) {
              draft.rightOverhang = 0;
              draft.rightBevelCut = null;
            }
            break;
          default:
            assertExhaustiveSwitch(endToAdjust);
        }
      });

      // Avoid creating zedge envelopes
      if (point2DEquals(trimmedEnvelope.leftPoint, trimmedEnvelope.rightPoint)) return null;

      if (isTrussEnvelope(trimmedEnvelope) && trimmedEnvelope.componentType === 'RoofTruss') {
        const ecosystem = getGeometricEcosystemForActivePortionOfLayout(getState());
        const roofAreas = StageTwoTrussEnvelope.narrowRoofAreasForTopBottomCutsAndCapPlanes(ecosystem, trimmedEnvelope);

        // Calculate which portion of the envelope is within a roof area, in order to discover which part should be
        // reclassified as overhang.
        const envelopeSpan = new Span(0, point2DDistanceTo(trimmedEnvelope.leftPoint, trimmedEnvelope.rightPoint));
        const envelopeSpansInRoof = (['Front', 'Center', 'Back'] as const)
          .map((justification) => {
            const segment = getEnvelopeSegmentFromEnvelope(trimmedEnvelope, justification)!;
            return reduceAndSortSpans(
              roofAreas.flatMap((roofArea) => Span.fromIntersectionsOfLineAndPolygon(segment.toLine(), roofArea)),
            )
              .map((roofSpan) => roofSpan.intersect(envelopeSpan))
              .filter(notNullish);
          })
          .reduce(intersectSpanSets);

        // 1 is the common case. For other cases, don't mess with the envelope more than we need to.
        if (envelopeSpansInRoof.length !== 1) return trimmedEnvelope;
        const envelopeSpanInRoof = envelopeSpansInRoof[0];

        // The portions missing from the in-roof span are the overhangs
        const leftOverhang = envelopeSpanInRoof.start - envelopeSpan.start;
        const rightOverhang = envelopeSpan.end - envelopeSpanInRoof.end;

        const envelopeRightDirection = Direction2D.fromPointObjects(
          trimmedEnvelope.leftPoint,
          trimmedEnvelope.rightPoint,
        );

        // Apply only the overhang on the adjusted side—leave the other side be
        return produce(trimmedEnvelope, (draft) => {
          switch (endToAdjust) {
            case 'left':
              draft.leftOverhang = leftOverhang;
              draft.leftPoint = Point2D.fromObject(draft.leftPoint)
                .translate(envelopeRightDirection.toVector(leftOverhang))
                .toObject();
              break;
            case 'right':
              draft.rightOverhang = rightOverhang;
              draft.rightPoint = Point2D.fromObject(draft.rightPoint)
                .translate(envelopeRightDirection.toVector(-rightOverhang))
                .toObject();
              break;
            default:
              assertExhaustiveSwitch(endToAdjust);
          }
        });
      } else {
        // Bearing envelopes and non-roof truss envelopes need no overhang adjustment
        return trimmedEnvelope;
      }
    };

    const { trussEnvelopes, bearingEnvelopes } = getState().layout;
    const trimmedTrussEnvelopes = envelopes.trussEnvelopeGuids
      ?.map((guid) => trimSingleEnvelope(trussEnvelopes[guid]))
      .filter(notNullish);
    const trimmedBearingEnvelopes = envelopes.bearingEnvelopeGuids
      ?.map((guid) => trimSingleEnvelope(bearingEnvelopes[guid]))
      .filter(notNullish);

    if (trimmedTrussEnvelopes?.length) {
      await dispatch(saveTrussEnvelopes(trimmedTrussEnvelopes));
    }
    if (trimmedBearingEnvelopes?.length) {
      dispatch(layoutSlice.actions.bearingEnvelopesUpserted({ bearingEnvelopes: trimmedBearingEnvelopes }));
      await dispatch(saveBearingEnvelopes());
    }
  };

function isTrussEnvelope(envelope: TrussEnvelope | BearingEnvelope): envelope is TrussEnvelope {
  // Compile-time-checked value to distinguish truss envelopes from bearing envelopes
  const TRUSS_ENVELOPE_DISTINGUISHING_KEY = 'elevation' satisfies Exclude<keyof TrussEnvelope, keyof BearingEnvelope>;
  return TRUSS_ENVELOPE_DISTINGUISHING_KEY in envelope;
}

/**
 * Design (with matching) all passed envelopes without a linked component. If none, regenerate all selected envelopes.
 */
export const designOrRegenerateComponents =
  (envelopes: readonly TrussEnvelope[]): AsyncThunkActionVoid =>
  async (dispatch) => {
    const unlinkedEnvelopes = envelopes.filter(({ componentDesignGuid }) => componentDesignGuid == null);
    if (unlinkedEnvelopes.length) {
      await dispatch(bulkDesignTrussEnvelopes(unlinkedEnvelopes, DESIGNING_SELECTED_TRUSS_ENVELOPES_MESSAGE));
    } else {
      await dispatch(regenerateComponentsInEnvelopes(envelopes));
    }
  };

/**
 * Design (without matching) the single passed envelope without a linked component. If multiple or none, show an error.
 */
export const designNewComponent =
  (envelopes: readonly TrussEnvelope[]): AsyncThunkActionVoid =>
  async (dispatch) => {
    const unlinkedEnvelopes = envelopes.filter(({ componentDesignGuid }) => componentDesignGuid == null);
    if (unlinkedEnvelopes.length === 1) {
      await dispatch(
        designTrussEnvelope(unlinkedEnvelopes[0], { matchExistingDesigns: false, jumpToTrussInDesignView: false }),
      );
    } else {
      dispatch(enqueueError('Create new design expects a single unlinked truss envelope.'));
    }
  };

/** Link all passed envelopes without a linked component. If none, unlink all selected envelopes. */
export const linkOrUnlinkComponents =
  (envelopes: readonly TrussEnvelope[]): AsyncThunkActionVoid =>
  async (dispatch) => {
    const unlinkedEnvelopes = envelopes.filter(({ componentDesignGuid }) => componentDesignGuid == null);
    if (unlinkedEnvelopes.length) {
      dispatch(
        showSelectComponentDesignDialog({
          continuation: {
            type: 'associate-with-envelope',
            trussEnvelopeGuids: unlinkedEnvelopes.map(({ guid }) => guid),
          },
        }),
      );
    } else {
      await dispatch(saveTrussEnvelopes(envelopes.map((envelope) => ({ ...envelope, componentDesignGuid: null }))));
    }
  };
